import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import {
  useAccount,
  useReadContract,
  useReadContracts,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { formatUnits, parseUnits, type Address } from "viem";
import {
  setModalBuyTicket,
  setModalPaymentTicketSuccess,
  setModalGameRule,
} from "@/app/modal/actions";
import {
  hideLoading,
  showLoading,
} from "@/components/common/loading/store/slice";
import luckyDrawServices from "@/network/luckyDraw/services";
import { erc20ABI, luckyDrawABI } from "@/constants/abi";

interface TicketPurchaseData {
  roundId: string;
  ticketPrice: number;
  quantity: number;
  totalPrice: number;
}

const LUCKY_DRAW_ADDRESS = import.meta.env.VITE_LUCKY_DRAW_CONTRACT_ADDRESS as Address | undefined;
const MBC_TOKEN_ADDRESS = import.meta.env.VITE_MBC_ADDRESS as Address | undefined;

const useLuckyDrawTicket = (isActive: boolean = true) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();

  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [ticketData, setTicketData] = useState<TicketPurchaseData | null>(null);
  const [pendingTxHash, setPendingTxHash] = useState<Address | undefined>();

  // Read ticket price using wagmi
  const { data: ticketPriceRaw } = useReadContract({
    address: LUCKY_DRAW_ADDRESS,
    abi: luckyDrawABI,
    functionName: "getTicketPrice",
    query: {
      enabled: isActive && !!LUCKY_DRAW_ADDRESS,
      refetchInterval: 5000,
    },
  });

  // Read MBC decimals and allowance in parallel
  const { data: mbcData, refetch: refetchAllowance } = useReadContracts({
    contracts: [
      {
        address: MBC_TOKEN_ADDRESS,
        abi: erc20ABI,
        functionName: "decimals",
      },
      {
        address: MBC_TOKEN_ADDRESS,
        abi: erc20ABI,
        functionName: "allowance",
        args: address && LUCKY_DRAW_ADDRESS ? [address, LUCKY_DRAW_ADDRESS] : undefined,
      },
    ],
    query: {
      enabled: !!MBC_TOKEN_ADDRESS && !!address && !!LUCKY_DRAW_ADDRESS,
    },
  });

  const mbcDecimals = mbcData?.[0]?.result ?? 18;
  const currentAllowance = mbcData?.[1]?.result ?? 0n;

  // Format ticket price
  const ticketPrice = ticketPriceRaw !== undefined ? formatUnits(ticketPriceRaw, 18) : "0";

  // Write contract hooks
  const { writeContractAsync: writeApprove } = useWriteContract();
  const { writeContractAsync: writeBuyTicket } = useWriteContract();

  // Wait for transaction receipt
  const { isLoading: isWaitingForTx } = useWaitForTransactionReceipt({
    hash: pendingTxHash,
  });

  const isUserLoggedIn = useCallback(() => {
    const accessToken = localStorage.getItem("accessToken");
    return !!(address && isConnected && accessToken);
  }, [address, isConnected]);

  const checkAllowance = useCallback(
    async (amount: number): Promise<boolean> => {
      if (!MBC_TOKEN_ADDRESS || !address || !LUCKY_DRAW_ADDRESS) return false;

      try {
        const requiredAmount = parseUnits(amount.toString(), mbcDecimals);
        return currentAllowance >= requiredAmount;
      } catch (error) {
        console.error("checkAllowance error:", error);
        return false;
      }
    },
    [address, mbcDecimals, currentAllowance]
  );

  const approveToken = useCallback(
    async (amount: number) => {
      if (!MBC_TOKEN_ADDRESS || !address || !LUCKY_DRAW_ADDRESS) {
        throw new Error("Missing required data for approval");
      }

      setIsApproving(true);
      try {
        const amountToApprove = parseUnits(amount.toString(), mbcDecimals);

        const hash = await writeApprove({
          address: MBC_TOKEN_ADDRESS,
          abi: erc20ABI,
          functionName: "approve",
          args: [LUCKY_DRAW_ADDRESS, amountToApprove],
        });

        setPendingTxHash(hash);

        // Wait for transaction confirmation
        // The useWaitForTransactionReceipt hook will handle this
        // For now, we'll use a simple polling approach
        await new Promise<void>((resolve, reject) => {
          const checkReceipt = async () => {
            try {
              // Refetch allowance to confirm approval
              const { data } = await refetchAllowance();
              const newAllowance = data?.[1]?.result ?? 0n;
              if (newAllowance >= amountToApprove) {
                resolve();
              } else {
                setTimeout(checkReceipt, 1000);
              }
            } catch {
              setTimeout(checkReceipt, 1000);
            }
          };
          setTimeout(checkReceipt, 2000);
          // Timeout after 60 seconds
          setTimeout(() => reject(new Error("Approval timeout")), 60000);
        });

        setIsApproved(true);
        addToast("Token approval successful!", { appearance: "success" });
      } catch (error: any) {
        addToast(`Approval failed: ${error?.message || "Unknown error"}`, {
          appearance: "error",
        });
        throw error;
      } finally {
        setIsApproving(false);
        setPendingTxHash(undefined);
      }
    },
    [address, mbcDecimals, writeApprove, refetchAllowance, addToast]
  );

  const performTicketPurchase = useCallback(
    async (quantity: number): Promise<string | null> => {
      if (!LUCKY_DRAW_ADDRESS) return null;

      try {
        const hash = await writeBuyTicket({
          address: LUCKY_DRAW_ADDRESS,
          abi: luckyDrawABI,
          functionName: "buyTicket",
          args: [BigInt(quantity)],
        });

        setPendingTxHash(hash);

        // Wait for confirmation by polling
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 3000); // Wait 3 seconds for tx to be mined
        });

        return hash;
      } catch (error) {
        console.error("performTicketPurchase error:", error);
        return null;
      } finally {
        setPendingTxHash(undefined);
      }
    },
    [writeBuyTicket]
  );

  const handleBuyTicketClick = useCallback(
    (roundData: any, gameRule?: any) => {
      if (!isUserLoggedIn()) {
        openConnectModal?.();
        return;
      }

      const ticketPurchaseData: TicketPurchaseData = {
        roundId: roundData.id,
        ticketPrice: roundData.ticketPrice,
        quantity: 1,
        totalPrice: roundData.ticketPrice,
      };

      setTicketData(ticketPurchaseData);

      const hasAck = localStorage.getItem("lucky_draw_rules_ack");
      if (!hasAck) {
        dispatch(
          setModalGameRule({
            toggle: true,
            data: {
              rules: gameRule || null,
              buyPayload: {
                toggle: true,
                data: {
                  roundId: roundData.id,
                  roundNumber: roundData.roundNumber,
                  ticketPrice: roundData.ticketPrice,
                },
                quantity: 1,
              },
            },
          })
        );
        return;
      }

      dispatch(
        setModalBuyTicket({
          toggle: true,
          data: {
            roundId: roundData.id,
            roundNumber: roundData.roundNumber,
            ticketPrice: roundData.ticketPrice,
          },
          quantity: 1,
        })
      );
    },
    [isUserLoggedIn, openConnectModal, dispatch]
  );

  const handleTicketPurchase = useCallback(
    async (quantity: number, roundId?: string) => {
      const currentRoundId = roundId || ticketData?.roundId;

      if (!currentRoundId || !address) {
        addToast("Missing required data for ticket purchase", {
          appearance: "error",
        });
        return;
      }

      dispatch(showLoading());

      try {
        const totalAmountInMBC = parseFloat(ticketPrice) * quantity;

        const hasEnoughAllowance = await checkAllowance(totalAmountInMBC);

        if (!hasEnoughAllowance) {
          await approveToken(totalAmountInMBC);
        }

        const ticketPurchaseTxHash = await performTicketPurchase(quantity);

        if (!ticketPurchaseTxHash) {
          throw new Error("Ticket purchase transaction failed");
        }

        const response = await luckyDrawServices.buyTickets({
          txHash: ticketPurchaseTxHash,
          quantity,
          roundId: currentRoundId,
        });

        if (response.data.success) {
          dispatch(
            setModalBuyTicket({
              toggle: false,
              data: {},
            })
          );

          dispatch(
            setModalPaymentTicketSuccess({
              toggle: true,
              data: {
                tickets: response.data.data,
                totalAmount: totalAmountInMBC,
                roundId: String(currentRoundId),
              },
            })
          );

          // Notify other parts of the app to refresh current round info
          try {
            window.dispatchEvent(new CustomEvent("lucky_draw_refresh_round"));
          } catch {}

          addToast("Tickets purchased successfully!", {
            appearance: "success",
          });
        } else {
          throw new Error("Failed to purchase tickets");
        }
      } catch (error: any) {
        addToast(`Ticket purchase failed: ${error?.message || "Unknown error"}`, {
          appearance: "error",
        });
      } finally {
        dispatch(hideLoading());
      }
    },
    [
      address,
      ticketData,
      ticketPrice,
      checkAllowance,
      approveToken,
      performTicketPurchase,
      dispatch,
      addToast,
    ]
  );

  const resetState = useCallback(() => {
    setIsApproved(false);
    setTicketData(null);
  }, []);

  return {
    isUserLoggedIn,
    handleBuyTicketClick,
    handleTicketPurchase,
    isApproved,
    isApproving: isApproving || isWaitingForTx,
    ticketData,
    resetState,
    checkAllowance,
    approveToken,
    ticketPrice,
  };
};

export default useLuckyDrawTicket;
